/** Tab D — Agent Productivity report. */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, PieChart, Pie, Cell, Legend,
} from "recharts";
import { useDrillThrough } from "@/hooks/useDrillThrough";
import { COLORS } from "@/components/reports/reportTheme";
import { UserCog } from "lucide-react";
import { HEAT_TINT } from "@/components/reports/reportTheme";

// =============== Tab D — Agent Productivity ===============
export function AgentProductivityReport({ filters }) {
  const [data, setData] = useState(null);
  const drillTo = useDrillThrough();
  useEffect(() => {
    const params = { ...filters };
    Object.keys(params).forEach((k) => (params[k] === "all" || params[k] === "") && delete params[k]);
    api.get("/analytics/agents", { params }).then((r) => setData(r.data));
  }, [JSON.stringify(filters)]); // eslint-disable-line

  if (!data) return <div className="text-sm text-slate-400 py-8 text-center">Loading…</div>;
  const heatMax = Math.max(1, ...data.workload_heatmap.flatMap((row) => row.days.map((d) => d.count)));

  return (
    <div className="space-y-5" data-testid="agent-productivity-report">
      <Card className="border-slate-200" data-testid="agent-table-card">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-base flex items-center gap-1.5">
            <UserCog className="w-4 h-4 text-indigo-600" />Per-agent productivity
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="text-xs uppercase tracking-wider text-slate-500">
                <TableHead>Agent</TableHead>
                <TableHead className="text-right">Handled</TableHead>
                <TableHead className="text-right">Resolved</TableHead>
                <TableHead className="text-right">Avg FRT</TableHead>
                <TableHead className="text-right">Avg resolution</TableHead>
                <TableHead className="text-right">P95</TableHead>
                <TableHead className="text-right">Comments</TableHead>
                <TableHead>Flag</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.per_agent.map((a) => (
                <TableRow key={a.agent_email} className="hover:bg-slate-50 cursor-pointer" data-testid={`agent-row-${a.agent_email}`}
                          onClick={() => drillTo("owner_email", a.agent_email, { date_from: filters.date_from, date_to: filters.date_to })}
                          title="Open tickets handled by this agent">
                  <TableCell className="text-sm font-medium text-slate-800">{a.agent_name}</TableCell>
                  <TableCell className="text-right text-sm">{a.tickets_handled}</TableCell>
                  <TableCell className="text-right text-sm">{a.resolved}</TableCell>
                  <TableCell className="text-right text-xs font-mono text-slate-600">{a.avg_frt_min != null ? `${a.avg_frt_min}m` : "—"}</TableCell>
                  <TableCell className="text-right text-xs font-mono text-slate-600">{a.avg_resolution_h != null ? `${a.avg_resolution_h}h` : "—"}</TableCell>
                  <TableCell className="text-right text-xs font-mono text-slate-600">{a.p95_resolution_h != null ? `${a.p95_resolution_h}h` : "—"}</TableCell>
                  <TableCell className="text-right text-sm">{a.comments_count}</TableCell>
                  <TableCell>
                    {a.flag === "top" && <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">Top performer</Badge>}
                    {a.flag === "needs_support" && <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">Needs support</Badge>}
                  </TableCell>
                </TableRow>
              ))}
              {data.per_agent.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center text-sm text-slate-400 py-8">No agent activity in this window</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {data.workload_heatmap.length > 0 && (
        <Card className="border-slate-200" data-testid="workload-heatmap-card">
          <CardHeader className="pb-2"><CardTitle className="font-heading text-base">Workload heatmap (top 10 agents)</CardTitle></CardHeader>
          <CardContent className="p-4 overflow-x-auto">
            <table className="text-[10px] min-w-full">
              <thead>
                <tr>
                  <th className="text-left text-slate-500 pr-3 pb-1 font-medium">Agent</th>
                  {data.days_in_window.map((d) => (
                    <th key={d} className="text-slate-400 font-normal px-0.5 pb-1">
                      <span className="inline-block transform -rotate-45 origin-bottom-left">{d.slice(5)}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.workload_heatmap.map((row) => (
                  <tr key={row.agent_email}>
                    <td className="text-slate-700 pr-3 py-0.5 font-medium">{row.agent}</td>
                    {row.days.map((d) => (
                      <td key={d.date} title={`${row.agent} on ${d.date}: ${d.count} ticket(s)`}>
                        <div className="w-5 h-5 rounded-sm border border-slate-100 m-px flex items-center justify-center text-[8px] text-slate-600"
                             style={{ backgroundColor: HEAT_TINT(d.count, heatMax) }}>
                          {d.count > 0 ? d.count : ""}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

